// src/services/questionnaires.ts
import { api } from "@/lib/apiClient";
import type {
  Questionnaire,
  QuestionnaireCloneVersionIn,
  QuestionnaireCreateIn,
  QuestionnaireDimension,
  QuestionnaireOption,
  QuestionnaireOptionUpsertIn,
  QuestionnaireQuestion,
  QuestionnaireQuestionUpsertIn,
  QuestionnaireSection,
  QuestionnaireSectionUpsertIn,
  QuestionnaireUpdateIn,
  QuestionnaireVersion,
  QuestionnaireVersionCreateIn,
  QuestionnaireVersionDetail,
} from "@/types/gro";

export type QuestionnaireScope = "tenant_model" | "client";

export async function listQuestionnaires(params?: {
  include_inactive?: boolean;
  q?: string;
  scope?: QuestionnaireScope;
}) {
  return api.get<Questionnaire[]>("/questionnaires", {
    params: {
      include_inactive: params?.include_inactive ?? false,
      q: params?.q || undefined,
      scope: params?.scope || undefined,
    },
  });
}

export async function getQuestionnaire(
  id: string,
  params?: { scope?: QuestionnaireScope },
) {
  return api.get<Questionnaire>(`/questionnaires/${id}`, {
    params: {
      scope: params?.scope || undefined,
    },
  });
}

export async function createQuestionnaire(
  payload: QuestionnaireCreateIn,
  params?: { scope?: QuestionnaireScope },
) {
  return api.post<Questionnaire>("/questionnaires", payload, {
    params: {
      scope: params?.scope || undefined,
    },
  });
}

export async function updateQuestionnaire(
  id: string,
  payload: QuestionnaireUpdateIn,
  params?: { scope?: QuestionnaireScope },
) {
  return api.put<Questionnaire>(`/questionnaires/${id}`, payload, {
    params: {
      scope: params?.scope || undefined,
    },
  });
}

export async function importQuestionnaireModelToClient(questionnaireId: string) {
  return api.post<Questionnaire>(`/questionnaires/${questionnaireId}/import-to-client`, {});
}

export async function listQuestionnaireVersions(questionnaireId: string) {
  return api.get<QuestionnaireVersion[]>(
    `/questionnaires/${questionnaireId}/versions`,
  );
}

export async function getQuestionnaireVersion(versionId: string) {
  return api.get<QuestionnaireVersionDetail>(
    `/questionnaires/versions/${versionId}`,
  );
}

export async function createQuestionnaireVersion(
  questionnaireId: string,
  payload: QuestionnaireVersionCreateIn,
) {
  return api.post<QuestionnaireVersionDetail>(
    `/questionnaires/${questionnaireId}/versions`,
    payload,
  );
}

export async function cloneQuestionnaireVersion(
  versionId: string,
  payload: QuestionnaireCloneVersionIn,
) {
  return api.post<QuestionnaireVersionDetail>(
    `/questionnaires/versions/${versionId}/clone`,
    payload,
  );
}

export async function publishQuestionnaireVersion(versionId: string) {
  return api.post<QuestionnaireVersionDetail>(
    `/questionnaires/versions/${versionId}/publish`,
    {},
  );
}

export async function createQuestionnaireSection(
  versionId: string,
  payload: QuestionnaireSectionUpsertIn,
) {
  return api.post<QuestionnaireSection>(
    `/questionnaires/versions/${versionId}/sections`,
    payload,
  );
}

export async function updateQuestionnaireSection(
  sectionId: string,
  payload: QuestionnaireSectionUpsertIn,
) {
  return api.put<QuestionnaireSection>(
    `/questionnaires/sections/${sectionId}`,
    payload,
  );
}

export async function deleteQuestionnaireSection(sectionId: string) {
  return api.delete<void>(`/questionnaires/sections/${sectionId}`);
}

export async function createQuestionnaireQuestion(
  versionId: string,
  payload: QuestionnaireQuestionUpsertIn,
) {
  return api.post<QuestionnaireQuestion>(
    `/questionnaires/versions/${versionId}/questions`,
    payload,
  );
}

export async function updateQuestionnaireQuestion(
  questionId: string,
  payload: QuestionnaireQuestionUpsertIn,
) {
  return api.put<QuestionnaireQuestion>(
    `/questionnaires/questions/${questionId}`,
    payload,
  );
}

export async function deleteQuestionnaireQuestion(questionId: string) {
  return api.delete<void>(`/questionnaires/questions/${questionId}`);
}

export async function createQuestionnaireOption(
  questionId: string,
  payload: QuestionnaireOptionUpsertIn,
) {
  return api.post<QuestionnaireOption>(
    `/questionnaires/questions/${questionId}/options`,
    payload,
  );
}

export async function updateQuestionnaireOption(
  optionId: string,
  payload: QuestionnaireOptionUpsertIn,
) {
  return api.put<QuestionnaireOption>(
    `/questionnaires/options/${optionId}`,
    payload,
  );
}

export async function deleteQuestionnaireOption(optionId: string) {
  return api.delete<void>(`/questionnaires/options/${optionId}`);
}
